import React from "react";
import { MagnifyingGlass, LinkSimple } from "@phosphor-icons/react";
import Input from "./Input";
import Textarea from "./Textarea";
import { cn } from "./cn";

/* The storefront host shown in the preview — change to your live domain. */
const DEFAULT_HOST = "www.technifro.ma";

const TITLE_MAX = 60;
const DESC_MAX = 160;

const Counter = ({ value = "", max, min = 0 }) => {
  const len = (value || "").length;
  const ok = len >= min && len <= max;
  const tone = len === 0 ? "text-faint" : len > max ? "text-red-500" : ok ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400";
  return (
    <span className={cn("text-[11px] font-medium tabular-nums", tone)}>
      {len}/{max}
    </span>
  );
};

/**
 * Reusable SEO block: slug + meta title/description with counters and a live
 * Google search-result (SERP) preview.
 *
 * value:    { slug, meta_title, meta_description }
 * onChange: (field, value) => void
 */
export default function SeoFields({
  value = {},
  onChange,
  fallbackTitle = "",
  fallbackDescription = "",
  basePath = "/",
  host = DEFAULT_HOST,
  showSlug = true,
}) {
  const { slug = "", meta_title = "", meta_description = "" } = value;
  const set = (field) => (e) => onChange?.(field, typeof e === "string" ? e : e.target.value);

  const previewTitle = (meta_title || fallbackTitle || "Page title").slice(0, 65);
  const previewDesc = (meta_description || fallbackDescription || "Add a meta description to control how this page appears in search results.").slice(0, 165);
  const cleanPath = `${basePath}${slug || "your-slug"}`.replace(/\/{2,}/g, "/");

  return (
    <section className="rounded-2xl border border-line bg-surface p-5">
      <div className="mb-1 flex items-center gap-2">
        <MagnifyingGlass size={16} className="text-muted" />
        <h2 className="text-sm font-semibold text-ink">SEO</h2>
      </div>
      <p className="mb-4 text-xs text-muted">How this page appears in search engines.</p>

      {/* SERP preview */}
      <div className="mb-5 rounded-xl border border-line bg-surface-2 p-4">
        <div className="flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400">
          <span className="truncate">{host}</span>
          <span className="truncate text-muted">{cleanPath}</span>
        </div>
        <div className="mt-1 truncate text-base font-medium text-[#1a0dab] dark:text-[#8ab4f8]">{previewTitle}</div>
        <div className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-muted">{previewDesc}</div>
      </div>

      <div className="space-y-4">
        {showSlug && (
          <Input
            label="URL slug"
            icon={LinkSimple}
            value={slug}
            onChange={set("slug")}
            placeholder="my-page-slug"
            hint="Leave blank to auto-generate from the name."
          />
        )}

        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <label className="text-start text-sm font-medium text-ink/90">Meta title</label>
            <Counter value={meta_title} max={TITLE_MAX} min={20} />
          </div>
          <Input value={meta_title} onChange={set("meta_title")} placeholder={fallbackTitle || "Title shown in search results"} />
        </div>

        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <label className="text-start text-sm font-medium text-ink/90">Meta description</label>
            <Counter value={meta_description} max={DESC_MAX} min={70} />
          </div>
          <Textarea value={meta_description} onChange={set("meta_description")} rows={3} placeholder="A short, compelling summary (around 150 characters) shown under the title in search results." />
        </div>
      </div>
    </section>
  );
}
